import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { Plus, Edit, Trash2, Save, X, LogOut, Lock, Eye, EyeOff, Image } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const AdminPage = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [password, setPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loginError, setLoginError] = useState('');
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(false);
  const [editingPost, setEditingPost] = useState(null);
  const [isCreating, setIsCreating] = useState(false);

  const [formData, setFormData] = useState({
    title: '',
    slug: '',
    content: '',
    excerpt: '',
    featured_image: '',
    published: true
  });

  useEffect(() => {
    const savedAuth = sessionStorage.getItem('adminAuth');
    if (savedAuth === 'true') {
      setIsLoggedIn(true);
      fetchPosts();
    }
  }, []);

  const handleLogin = async (e) => {
    e.preventDefault();
    setLoginError('');
    
    try {
      await axios.post(`${API}/admin/login`, { password });
      setIsLoggedIn(true);
      sessionStorage.setItem('adminAuth', 'true');
      fetchPosts();
    } catch (err) {
      setLoginError('Invalid password');
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    sessionStorage.removeItem('adminAuth');
  };

  const fetchPosts = async () => {
    setLoading(true);
    try {
      const response = await axios.get(`${API}/blog/all`);
      setPosts(response.data);
    } catch (error) {
      console.error('Error fetching posts:', error);
    } finally {
      setLoading(false);
    }
  };

  const generateSlug = (title) => {
    return title
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    const newValue = type === 'checkbox' ? checked : value;
    
    setFormData(prev => {
      const updated = { ...prev, [name]: newValue };
      if (name === 'title' && !editingPost) {
        updated.slug = generateSlug(value);
      }
      return updated;
    });
  };

  const resetForm = () => {
    setFormData({
      title: '',
      slug: '',
      content: '',
      excerpt: '',
      featured_image: '',
      published: true
    });
    setEditingPost(null);
    setIsCreating(false);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API}/admin/blog`, formData);
      fetchPosts();
      resetForm();
    } catch (error) {
      console.error('Error creating post:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleUpdate = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.put(`${API}/admin/blog/${editingPost.id}`, formData);
      fetchPosts();
      resetForm();
    } catch (error) {
      console.error('Error updating post:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async (postId) => {
    if (!window.confirm('Are you sure you want to delete this post?')) return;
    
    try {
      await axios.delete(`${API}/admin/blog/${postId}`);
      fetchPosts();
    } catch (error) {
      console.error('Error deleting post:', error);
    }
  };

  const startEdit = (post) => {
    setEditingPost(post);
    setFormData({
      title: post.title,
      slug: post.slug,
      content: post.content,
      excerpt: post.excerpt,
      featured_image: post.featured_image || '',
      published: post.published
    });
    setIsCreating(false);
  };

  const startCreate = () => {
    resetForm();
    setIsCreating(true);
  };

  // Login screen
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-slate-100 flex items-center justify-center px-6" data-testid="admin-login">
        <motion.div 
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="bg-white rounded-2xl shadow-xl p-8 w-full max-w-md"
        >
          <div className="text-center mb-8">
            <div className="w-16 h-16 bg-[#1E3A8A] rounded-2xl flex items-center justify-center mx-auto mb-4">
              <Lock className="w-8 h-8 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-[#0F172A]">Admin Login</h1>
            <p className="text-slate-500 mt-2">Enter your password to continue</p>
          </div>

          <form onSubmit={handleLogin}>
            <div className="relative mb-6">
              <Input
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter password"
                className="pr-10"
                data-testid="admin-password-input"
              />
              <button
                type="button"
                onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
              >
                {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
              </button>
            </div>

            {loginError && (
              <p className="text-red-500 text-sm mb-4" data-testid="admin-login-error">{loginError}</p>
            )}

            <Button 
              type="submit" 
              className="w-full bg-[#1E3A8A] hover:bg-[#1D4ED8] text-white font-bold py-3"
              data-testid="admin-login-btn"
            >
              Login
            </Button>
          </form>
        </motion.div>
      </div>
    );
  }

  // Admin dashboard
  return (
    <div className="min-h-screen bg-slate-100" data-testid="admin-dashboard">
      {/* Header */}
      <header className="bg-[#0F172A] text-white py-4 px-6 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto flex justify-between items-center">
          <h1 className="text-xl font-bold">Resources Admin</h1>
          <Button 
            onClick={handleLogout}
            variant="outline"
            className="border-white/20 text-white hover:bg-white/10"
            data-testid="admin-logout-btn"
          >
            <LogOut className="w-4 h-4 mr-2" /> Logout
          </Button>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Posts List */}
          <div className="lg:col-span-2">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-2xl font-bold text-[#0F172A]">Resource Posts</h2>
              <Button 
                onClick={startCreate}
                className="bg-[#DC2626] hover:bg-[#B91C1C] text-white"
                data-testid="admin-new-post-btn"
              >
                <Plus className="w-4 h-4 mr-2" /> New Post
              </Button>
            </div>

            {loading && posts.length === 0 ? (
              <div className="text-center py-12">
                <div className="w-12 h-12 border-4 border-[#1E3A8A] border-t-transparent rounded-full spinner mx-auto" />
              </div>
            ) : posts.length === 0 ? (
              <div className="bg-white rounded-xl p-8 text-center">
                <p className="text-slate-500">No posts yet. Create your first post!</p>
              </div>
            ) : (
              <div className="space-y-4">
                {posts.map((post) => (
                  <div 
                    key={post.id} 
                    className={`bg-white rounded-xl p-6 shadow-sm ${editingPost?.id === post.id ? 'ring-2 ring-[#1E3A8A]' : ''}`}
                    data-testid={`admin-post-${post.id}`}
                  >
                    <div className="flex justify-between items-start gap-4">
                      <div className="flex-1 min-w-0">
                        <div className="flex items-center gap-2 mb-2">
                          <h3 className="font-bold text-[#0F172A] truncate">{post.title}</h3>
                          {!post.published && (
                            <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">Draft</span>
                          )}
                        </div>
                        <p className="text-slate-500 text-sm truncate">{post.excerpt}</p>
                        <p className="text-slate-400 text-xs mt-2">
                          {new Date(post.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <div className="flex gap-2">
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={() => startEdit(post)}
                          data-testid={`admin-edit-${post.id}`}
                        >
                          <Edit className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="outline"
                          className="text-red-500 hover:bg-red-50"
                          onClick={() => handleDelete(post.id)}
                          data-testid={`admin-delete-${post.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Editor Panel */}
          <div className="lg:col-span-1">
            <div className="bg-white rounded-xl p-6 shadow-sm sticky top-24">
              <div className="flex justify-between items-center mb-6">
                <h2 className="text-lg font-bold text-[#0F172A]">
                  {editingPost ? 'Edit Post' : isCreating ? 'New Post' : 'Post Editor'}
                </h2>
                {(editingPost || isCreating) && (
                  <Button size="sm" variant="ghost" onClick={resetForm}>
                    <X className="w-4 h-4" />
                  </Button>
                )}
              </div>

              {!editingPost && !isCreating ? (
                <div className="text-center py-8">
                  <p className="text-slate-500 mb-4">Select a post to edit or create a new one</p>
                  <Button onClick={startCreate} className="bg-[#1E3A8A] hover:bg-[#1D4ED8] text-white">
                    <Plus className="w-4 h-4 mr-2" /> Create Post
                  </Button>
                </div>
              ) : (
                <form onSubmit={editingPost ? handleUpdate : handleCreate} className="space-y-4">
                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-1">Title</label>
                    <Input
                      name="title"
                      value={formData.title}
                      onChange={handleChange}
                      required
                      data-testid="admin-title-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-1">Slug</label>
                    <Input
                      name="slug"
                      value={formData.slug}
                      onChange={handleChange}
                      required
                      data-testid="admin-slug-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-1">Featured Image URL</label>
                    <div className="relative">
                      <Input
                        name="featured_image"
                        value={formData.featured_image}
                        onChange={handleChange}
                        placeholder="https://..."
                        className="pl-10"
                        data-testid="admin-image-input"
                      />
                      <Image className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-1">Excerpt</label>
                    <Textarea
                      name="excerpt"
                      value={formData.excerpt}
                      onChange={handleChange}
                      rows={2}
                      required
                      data-testid="admin-excerpt-input"
                    />
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-[#0F172A] mb-1">Content (HTML)</label>
                    <Textarea
                      name="content"
                      value={formData.content}
                      onChange={handleChange}
                      rows={8}
                      required
                      className="font-mono text-sm"
                      data-testid="admin-content-input"
                    />
                  </div>

                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      id="published"
                      name="published"
                      checked={formData.published}
                      onChange={handleChange}
                      className="rounded border-slate-300"
                      data-testid="admin-published-checkbox"
                    />
                    <label htmlFor="published" className="text-sm text-slate-600">Published</label>
                  </div>

                  <div className="flex gap-2 pt-4">
                    <Button
                      type="submit"
                      disabled={loading}
                      className="flex-1 bg-[#1E3A8A] hover:bg-[#1D4ED8] text-white"
                      data-testid="admin-save-btn"
                    >
                      <Save className="w-4 h-4 mr-2" />
                      {loading ? 'Saving...' : 'Save'}
                    </Button>
                    <Button type="button" variant="outline" onClick={resetForm}>
                      Cancel
                    </Button>
                  </div>
                </form>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminPage;
